"use client"

import { useEffect, useState } from "react"
import { IconTrendingDown, IconTrendingUp } from "@tabler/icons-react"

import { Badge } from "@/components/ui/badge"
import {
  Card,
  CardAction,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { getDashboardStats } from "@/lib/api-client"

interface DashboardStats {
  qr_scans: number
  active_players: number
  games_played: number
  data_points_count: number
}

// Format number to K/M notation
function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M'
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K'
  }
  return num.toLocaleString()
}

export function SectionCards() {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetchStats() {
      try {
        setLoading(true)
        const data = await getDashboardStats() as DashboardStats
        setStats(data)
      } catch (err) {
        console.error('Failed to fetch dashboard stats:', err)
        setError('Failed to load statistics')
      } finally {
        setLoading(false)
      }
    }

    fetchStats()
  }, [])

  // Default values for loading/error states
  const displayStats = stats || {
    qr_scans: 0,
    active_players: 0,
    games_played: 0,
    data_points_count: 0
  }

  return (
    <div className="*:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card grid grid-cols-1 gap-6 *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:shadow-xs sm:grid-cols-2 @xl/main:grid-cols-2 h-full min-h-[calc(100vh-12rem)]">
      <Card className="@container/card flex flex-col">
        <CardHeader className="flex-1">
          <CardDescription className="text-lg">QR Code Scans</CardDescription>
          <CardTitle className="text-7xl md:text-8xl lg:text-9xl font-black tabular-nums leading-none">
            {loading ? '...' : formatNumber(displayStats.qr_scans)}
          </CardTitle>
          <CardAction>
            <Badge variant="outline" className="text-lg py-2 px-3">
              <IconTrendingUp className="size-6" />
              All time
            </Badge>
          </CardAction>
        </CardHeader>
        <CardFooter className="flex-col items-start gap-3 text-lg">
          <div className="line-clamp-1 flex gap-2 font-bold text-xl">
            {loading ? 'Loading...' : error ? 'Error loading' : 'Total QR scans'} <IconTrendingUp className="size-6" />
          </div>
          <div className="text-muted-foreground text-lg">
            Total scans across all campaigns
          </div>
        </CardFooter>
      </Card>
      <Card className="@container/card flex flex-col">
        <CardHeader className="flex-1">
          <CardDescription className="text-lg">Active Players</CardDescription>
          <CardTitle className="text-7xl md:text-8xl lg:text-9xl font-black tabular-nums leading-none">
            {loading ? '...' : formatNumber(displayStats.active_players)}
          </CardTitle>
          <CardAction>
            <Badge variant="outline" className="text-lg py-2 px-3">
              <IconTrendingUp className="size-6" />
              Registered
            </Badge>
          </CardAction>
        </CardHeader>
        <CardFooter className="flex-col items-start gap-3 text-lg">
          <div className="line-clamp-1 flex gap-2 font-bold text-xl">
            {loading ? 'Loading...' : error ? 'Error loading' : 'Total players'} <IconTrendingUp className="size-6" />
          </div>
          <div className="text-muted-foreground text-lg">
            Registered gamers
          </div>
        </CardFooter>
      </Card>
      <Card className="@container/card flex flex-col">
        <CardHeader className="flex-1">
          <CardDescription className="text-lg">Games Played</CardDescription>
          <CardTitle className="text-7xl md:text-8xl lg:text-9xl font-black tabular-nums leading-none">
            {loading ? '...' : formatNumber(displayStats.games_played)}
          </CardTitle>
          <CardAction>
            <Badge variant="outline" className="text-lg py-2 px-3">
              <IconTrendingUp className="size-6" />
              All time
            </Badge>
          </CardAction>
        </CardHeader>
        <CardFooter className="flex-col items-start gap-3 text-lg">
          <div className="line-clamp-1 flex gap-2 font-bold text-xl">
            {loading ? 'Loading...' : error ? 'Error loading' : 'Total games played'} <IconTrendingUp className="size-6" />
          </div>
          <div className="text-muted-foreground text-lg">
            Total game sessions
          </div>
        </CardFooter>
      </Card>
      <Card className="@container/card flex flex-col">
        <CardHeader className="flex-1">
          <CardDescription className="text-lg">Data Points Captured</CardDescription>
          <CardTitle className="text-7xl md:text-8xl lg:text-9xl font-black tabular-nums leading-none">
            {loading ? '...' : formatNumber(displayStats.data_points_count)}
          </CardTitle>
          <CardAction>
            <Badge variant="outline" className="text-lg py-2 px-3">
              <IconTrendingUp className="size-6" />
              With contact info
            </Badge>
          </CardAction>
        </CardHeader>
        <CardFooter className="flex-col items-start gap-3 text-lg">
          <div className="line-clamp-1 flex gap-2 font-bold text-xl">
            {loading ? 'Loading...' : error ? 'Error loading' : 'Customer profiles'} <IconTrendingUp className="size-6" />
          </div>
          <div className="text-muted-foreground text-lg">
            Phone, email, social
          </div>
        </CardFooter>
      </Card>
    </div>
  )
}
